import type { FlightItinerarySegment } from "@b2bportal/air-booking-api";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { decodeCityName } from "@overrides/utilities";
import { AirlineIcon } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import styles from "./FlightDetailsPanel.module.scss";
import { parseSegmentLocalDate } from "@/utilities/flights/segmentDateTime";

interface FlightSegmentCardProps {
  className?: string;
  segment: FlightItinerarySegment;
  airportMap?: Record<
    string,
    { name: string; code: string; cityName?: string }
  >;
  airlineMap?: Record<string, { displayName: string }>;
  /** When provided (slice with layover), card shows final destination and total duration including layovers */
  lastSegment?: FlightItinerarySegment;
  totalDurationMinutes?: number;
  stopsCount?: number;
  /** When provided (return slice), card shows this as "Departure to X" (trip origin) instead of last segment destination */
  displayDestinationCode?: string;
}

export const FlightSegmentCard = ({
  className,
  segment,
  airportMap,
  airlineMap,
  lastSegment,
  totalDurationMinutes,
  stopsCount,
  displayDestinationCode,
}: FlightSegmentCardProps) => {
  const [, cn] = useModuleBEM(styles, "FlightDetailsPanel");
  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const destinationSegment = lastSegment ?? segment;
  const destinationCode =
    displayDestinationCode ??
    destinationSegment.destination?.locationCode ??
    "";
  const departureDateStr =
    segment.zonedScheduledDeparture ?? segment.scheduledDeparture ?? "";

  const departureTime = departureDateStr
    ? formatDateTime(
        parseSegmentLocalDate(departureDateStr),
        DateTimeFormatStyle.ShortTime
      )
    : "";

  const duration = (() => {
    if (totalDurationMinutes !== undefined && totalDurationMinutes >= 0) {
      const hours = Math.floor(totalDurationMinutes / 60);
      const mins = totalDurationMinutes % 60;
      return `${hours}h ${mins}m`;
    }
    const arrivalDateStr =
      segment.zonedScheduledArrival ?? segment.scheduledArrival ?? "";
    if (!departureDateStr || !arrivalDateStr) return "";
    const minutes = dayjs(arrivalDateStr).diff(
      dayjs(departureDateStr),
      "minutes"
    );
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  })();

  const departureDateFormatted = departureDateStr
    ? formatDateTime(
        parseSegmentLocalDate(departureDateStr),
        DateTimeFormatStyle.ShortMonthDayShortWeekday
      )
    : "";

  const destinationName = decodeCityName(
    airportMap?.[destinationCode]?.cityName,
    airportMap?.[destinationCode]?.name,
    destinationCode
  );

  const airlineCode = segment.marketingAirline?.code ?? "";
  const flightNumber = segment.marketingAirline?.flightNumber ?? "";
  const airlineName = airlineMap?.[airlineCode]?.displayName ?? airlineCode;
  const airlineInfo = `${airlineName} • ${airlineCode}${flightNumber}`;

  return (
    <div className={clsx(cn("segment-card"), className)}>
      <div className={cn("segment-header")}>
        <Trans
          i18nKey="flightTrips.flightDetailsPanelHeader"
          values={{
            location: destinationName,
            date: departureDateFormatted,
          }}
          components={[<b />]}
          // City names are from trusted backend API and have been HTML-decoded
          tOptions={{ interpolation: { escapeValue: false } }}
        />
      </div>

      <div className={cn("segment-content")}>
        <div className={cn("times-container")}>
          <div className={cn("time-info")}>
            <p className={cn("time")}>{departureTime}</p>
            <div className={cn("airline-info")}>
              <AirlineIcon
                airlineCode={airlineCode}
                altText={airlineName}
                className={cn("airline-icon")}
                size="small"
              />
              <span className={cn("airline-text")}>{airlineInfo}</span>
            </div>
          </div>

          <div className={cn("flight-details")}>
            <p className={cn("duration")}>{duration}</p>
            <p className={cn("stops")}>
              {stopsCount !== undefined
                ? stopsCount === 0
                  ? t("stopDetails.nonstop")
                  : t("stopDetails.stop", { count: stopsCount })
                : segment.stops === 0
                  ? t("stopDetails.nonstop")
                  : t("stopDetails.stop", { count: segment.stops })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
